// src/components/Login.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaSignInAlt, FaTimes } from 'react-icons/fa';
import { useBaseUrl } from '../Contexts/BaseUrlContext';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const navigate = useNavigate();
  const { baseUrl } = useBaseUrl(); // e.g. http://localhost/loandestination/system/api/

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    if (!formData.email || !formData.password) {
      setErrorMessage('Please fill in both email and password.');
      setIsLoading(false);
      return;
    }

    try {
      // 1) Login request (action=login, email, password)
      const loginForm = new FormData();
      loginForm.append('action', 'login');
      loginForm.append('email', formData.email);
      loginForm.append('password', formData.password);

      const loginResp = await axios.post(
        `${baseUrl}bank-system/auth.php`,
        loginForm,
        { withCredentials: true }
      );

      console.log('Login API Response:', loginResp.data);

      // The API example returns: { status: true, message: "Login successful", token: "..." }
      if (loginResp.data && loginResp.data.status === true) {
        const token = loginResp.data.token;
        // Save token
        localStorage.setItem('authToken', token);
        setSuccessMessage(loginResp.data.message || 'Login successful');

        // 2) Auth-check request (action=auth-check). Include token so server can validate.
        try {
          const authForm = new FormData();
          authForm.append('action', 'auth-check');
          // include token (server might expect it in form-data or headers; we're including both to be safe)
          authForm.append('token', token);

          const authResp = await axios.post(
            `${baseUrl}bank-system/auth.php`,
            authForm,
            {
              withCredentials: true,
              headers: {
                // also set Authorization header in case server reads it from headers
                Authorization: `Bearer ${token}`,
              },
            }
          );

          console.log('Auth-check Response:', authResp.data);

          // Example auth-check response: { access: true, id: "17631992794", role: "Bank & Finance", bank_name: "Avanse" }
          if (authResp.data && authResp.data.access === true) {
            const userData = {
              id: authResp.data.id,
              role: authResp.data.role,
              bank_name: authResp.data.bank_name,
            };
            // store user data
            localStorage.setItem('userData', JSON.stringify(userData));

            // navigate to dashboard
            setTimeout(() => {
              navigate('/dashboard');
            }, 600);
          } else {
            // if auth-check failed, show message but keep token saved (optional)
            setErrorMessage(authResp.data.message || 'Auth check failed. Please try again.');
            // optionally clear token if needed:
            // localStorage.removeItem('authToken');
          }
        } catch (authError) {
          console.error('Auth-check error:', authError.response?.data || authError.message);
          const msg = authError.response?.data?.message || 'Auth check failed. Please try again.';
          setErrorMessage(msg);
        }
      } else {
        // login failed
        setErrorMessage(loginResp.data?.message || 'Login failed. Please try again.');
      }
    } catch (error) {
      console.error('Login error:', error.response?.data || error.message);
      const errorMsg = error.response?.data?.message || 'An error occurred. Please check your connection or try again later.';
      setErrorMessage(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full min-h-screen flex items-center justify-center bg-gray-100 animate-fadeIn">
      <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full">
        <img src="loan_logo.jpg" alt="Loan Destination" className="w-full mx-auto mb-6" />
        {/* <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center flex items-center justify-center">
          <FaSignInAlt className="mr-2 text-blue-600" /> Bank Login
        </h2> */}

        {/* Error Message */}
        {errorMessage && (
          <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-lg flex items-center">
            <FaTimes className="mr-2" /> {errorMessage}
          </div>
        )}

        {/* Success Message */}
        {successMessage && (
          <div className="mb-4 p-4 bg-green-100 text-green-700 rounded-lg flex items-center">
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            {successMessage}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-300"
              placeholder="Your Email ID"
              required
              autoComplete="email"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Password</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-300"
              placeholder="Enter your password"
              required
              autoComplete="current-password"
            />
          </div>
          <button
            type="submit"
            className="w-full flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition duration-300 shadow-md disabled:opacity-50"
            disabled={isLoading}
          >
            {isLoading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-2"></div>
            ) : (
              <FaSignInAlt className="mr-2" />
            )}
            {isLoading ? 'Logging in...' : 'Bank Login'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;
